import { baseApi } from "@/services/api";
import type {
  ApiEmployeeDepartment,
  ArchiveEmployeeDepartmentResponse,
  RestoreEmployeeDepartmentResponse,
  CreateEmployeeDepartmentRequest,
  CreateEmployeeDepartmentResponse,
  ListEmployeeDepartmentsResponse,
  UpdateEmployeeDepartmentRequest,
  UpdateEmployeeDepartmentResponse,
} from "./employeeDepartmentTypes";

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
}

function unwrap<T>(raw: ApiEnvelope<T>, fallbackMessage: string): T {
  if (!raw || raw.success !== true || !raw.data) {
    throw new Error(raw?.message || fallbackMessage);
  }
  return raw.data;
}

export const employeeDepartmentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listEmployeeDepartments: builder.query<
      ListEmployeeDepartmentsResponse,
      { status?: "active" | "archived" } | void
    >({
      query: (params) => ({
        url: "/sa/employee-departments",
        method: "GET",
        params: params?.status ? { status: params.status } : undefined,
      }),
      transformResponse: (raw: ApiEnvelope<ListEmployeeDepartmentsResponse>) =>
        unwrap(raw, "Failed to load employee departments"),
      providesTags: ["EmployeeDepartments"],
    }),
    createEmployeeDepartment: builder.mutation<
      CreateEmployeeDepartmentResponse,
      CreateEmployeeDepartmentRequest
    >({
      query: (body) => ({
        url: "/sa/employee-departments",
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<CreateEmployeeDepartmentResponse>) =>
        unwrap(raw, "Failed to create employee department"),
      invalidatesTags: ["EmployeeDepartments"],
    }),
    updateEmployeeDepartment: builder.mutation<
      UpdateEmployeeDepartmentResponse,
      UpdateEmployeeDepartmentRequest
    >({
      query: ({ departmentId, ...body }) => ({
        url: `/sa/employee-departments/${encodeURIComponent(departmentId)}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<UpdateEmployeeDepartmentResponse>) =>
        unwrap(raw, "Failed to update employee department"),
      invalidatesTags: ["EmployeeDepartments"],
    }),
    archiveEmployeeDepartment: builder.mutation<
      ArchiveEmployeeDepartmentResponse,
      string
    >({
      query: (departmentId) => ({
        url: `/sa/employee-departments/${encodeURIComponent(departmentId)}/archive`,
        method: "POST",
      }),
      transformResponse: (raw: ApiEnvelope<ArchiveEmployeeDepartmentResponse>) =>
        unwrap(raw, "Failed to archive employee department"),
      invalidatesTags: ["EmployeeDepartments", "Employees"],
    }),
    restoreEmployeeDepartment: builder.mutation<
      RestoreEmployeeDepartmentResponse,
      string
    >({
      query: (departmentId) => ({
        url: `/sa/employee-departments/${encodeURIComponent(departmentId)}/restore`,
        method: "POST",
      }),
      transformResponse: (raw: ApiEnvelope<RestoreEmployeeDepartmentResponse>) =>
        unwrap(raw, "Failed to restore employee department"),
      invalidatesTags: ["EmployeeDepartments", "Employees"],
    }),
  }),
});

export const {
  useListEmployeeDepartmentsQuery,
  useCreateEmployeeDepartmentMutation,
  useUpdateEmployeeDepartmentMutation,
  useArchiveEmployeeDepartmentMutation,
  useRestoreEmployeeDepartmentMutation,
} = employeeDepartmentsApi;

export function isActiveDepartment(
  department: ApiEmployeeDepartment,
): boolean {
  return department.status === "active";
}

/** Active catalog display names in sort order (filters, mock role UI). */
export function activeDepartmentDisplayNames(
  departments: ApiEmployeeDepartment[],
): string[] {
  return departments
    .filter(isActiveDepartment)
    .sort(
      (a, b) =>
        (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
        a.displayName.localeCompare(b.displayName),
    )
    .map((dept) => dept.displayName);
}

const EMPTY_DEPARTMENT_LABEL = "—";

/** Catalog active names plus legacy row labels not in the catalog (e.g. C Suite). */
export function departmentFilterOptions(
  departments: ApiEmployeeDepartment[],
  rowDepartmentLabels: string[],
): string[] {
  const fromCatalog = activeDepartmentDisplayNames(departments);
  const seen = new Set(fromCatalog);
  const extras: string[] = [];

  for (const label of rowDepartmentLabels) {
    const trimmed = (label || "").trim();
    if (!trimmed || trimmed === EMPTY_DEPARTMENT_LABEL) continue;
    if (!seen.has(trimmed)) {
      seen.add(trimmed);
      extras.push(trimmed);
    }
  }

  extras.sort((a, b) => a.localeCompare(b));
  return [...fromCatalog, ...extras];
}

export function activeDepartmentSelectOptions(
  departments: ApiEmployeeDepartment[],
  currentDepartmentId?: string | null,
) {
  const active = departments.filter(isActiveDepartment);
  const options = [
    { value: "", label: "Select Department" },
    ...active.map((dept) => ({
      value: dept.departmentId,
      label: dept.displayName,
    })),
  ];

  const current = (currentDepartmentId || "").trim();
  if (current && !options.some((option) => option.value === current)) {
    const match = departments.find((dept) => dept.departmentId === current);
    options.push({
      value: current,
      label: match?.displayName || current,
    });
  }

  return options;
}
